import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { entitiesApi } from "@/api/counterparties";
import { useCreateProvider } from "@/hooks/capitalMarkets/useServiceProviders";
import { humanizeStatus } from "../shared/format";
import type { ProviderConfig } from "./providerConfig";

export function NewProviderModal({
  config,
  onClose,
  onCreated,
}: {
  config: ProviderConfig;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const create = useCreateProvider(config.type);

  const [entityId, setEntityId] = useState("");
  const [status, setStatus] = useState(config.statuses[0]);
  const [jurisdiction, setJurisdiction] = useState("DE");

  const entitiesQuery = useQuery({
    queryKey: ["counterparties", "entities", "picker"],
    queryFn: () => entitiesApi.list({ pageSize: 200 }),
  });
  const entities = useMemo(
    () => entitiesQuery.data?.data ?? [],
    [entitiesQuery.data],
  );

  const handleSave = () => {
    if (!entityId) {
      toast.error("Select the counterparty Entity first.");
      return;
    }
    // The four create-DTO shapes share entityId + status; TrustCompany also
    // requires a jurisdiction. Extra optional fields are set on the detail
    // page once the record exists.
    const base: Record<string, unknown> = { entityId, status };
    if (config.type === "TrustCompany") base.jurisdiction = jurisdiction || "DE";
    create
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .mutateAsync(base as any)
      .then((row) => {
        toast.success(`${config.singularLabel} created.`);
        onCreated((row as { id: string }).id);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`New ${config.singularLabel}`}
      description={`Create a reusable ${config.singularLabel.toLowerCase()} counterparty linked to an Entity.`}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} onClick={handleSave}>
            Create {config.singularLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Linked Entity" required>
          <Select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            aria-label="Linked Entity"
          >
            <option value="">Select an entity…</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.legalEntityName}
              </option>
            ))}
          </Select>
        </FormField>

        {config.type === "TrustCompany" && (
          <FormField label="Jurisdiction" required>
            <Input
              value={jurisdiction}
              maxLength={2}
              onChange={(e) => setJurisdiction(e.target.value.toUpperCase())}
              aria-label="Jurisdiction"
            />
          </FormField>
        )}

        <FormField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {config.statuses.map((s) => (
              <option key={s} value={s}>
                {humanizeStatus(s)}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
    </Modal>
  );
}
